import { FC, JSX, useEffect, useState } from 'react';
import { TEMPLATE_BASE_PATH } from '@/constants';
import { useEcomStoreSelector, useUiStore } from '@/data';
import { find, startCase } from 'lodash';
import { currencyFormat } from '@/utils';
import {
  useFeaturedFlag,
  useProductAvailability,
  useSelectedStore,
  useWishList,
} from '@/hooks';
import {
  Alert,
  //BundlePopup,
  Button,
  Counter,
  Icon,
  IconSvg,
  Link,
  Placeholder,
  Price,
  useData,
  WeightVariants,
} from '@/components';
import classNames from 'classnames';
import { GProduct, Variant } from '@/types';

import { useProductContext } from '../Product/context';
import { getUnitTierPricing } from '../Product/context/utils';
import { ChooseStoreButton } from './ChooseStoreButton';

export interface IProductControlsProps {
  product: GProduct;
  weightVariant: Variant;
  isSpecial: boolean;
  hasSelectedStore?: boolean;
}

const ProductControls: FC<IProductControlsProps> = ({
  product,
  weightVariant,
  isSpecial,
  hasSelectedStore = false,
}) => {
  const [, setState] = useUiStore();
  const { customer_type } = useEcomStoreSelector(['customer_type']);
  const { unitPricingTier } = useProductContext();
  const { store } = useSelectedStore();
  const hasStore = !!store?.short_name || hasSelectedStore;
  const { isProductAvailable, isCheckingAvailability } =
    useProductAvailability(product);

  const { treezStores } = useData();
  const { keepShoppingActive, wishlistActive, isMedAndRecSelectionActive } =
    useFeaturedFlag();

  const [isFavorite, setIsFavorite] = useState(false);
  const { favProductsIds, addFav, removeFav } = useWishList();
  const inStock = !product?.isOutStock && !product?.isLowStock;
  //const [bundleJaneSpecial, setBundleJaneSpecial] = useState<JaneSpecial>();
  //const [bundleProducts, setBundleProducts] = useState<GProduct[]>([]);
  //const [showBundlePopup, setShowBundlePopup] = useState(false);
  //const [bundleId, setBundleId] = useState<string>();
  const [currentWeightVariant, setCurrentWeightVariant] = useState<Variant>();
  const unitTierPricing = unitPricingTier
    ? getUnitTierPricing(unitPricingTier, product.pricing)
    : null;

  //Ensure that the state is updated correctly when the weight variant changes
  useEffect(() => {
    setCurrentWeightVariant(weightVariant);
  }, [weightVariant]);

  //const bundlePanelRef = useRef<HTMLDivElement>(null);

  /* const handleBundleCallback = (
    bundleSpecial: JaneSpecial,
    bundleProducts: GProduct[],
  ) => {
    setBundleJaneSpecial(bundleSpecial);
    setBundleProducts(bundleProducts);
    setShowBundlePopup(true);
  }; */

  const showStoresSelect = event => {
    event.stopPropagation();
    setState({ is_store_location_open: true });
  };

  const handleFavorite = (product: GProduct) => {
    if (isFavorite) {
      removeFav(product.id as string);
      return;
    }

    addFav(product.id as string);
  };

  useEffect(() => {
    const p = find(favProductsIds, id => id == product.id);
    if (p !== undefined) {
      setIsFavorite(true);
      return;
    }
    setIsFavorite(false);
  }, [favProductsIds, product]);

  /*   const handleBundlePopup = (id: string) => {
    setBundleId(id);
    setShowBundlePopup(true);
  }; */

  const warningMessagge = (isSingleStore: boolean): string => {
    let warning = 'Unavailable Product';

    if (isSingleStore && isMedAndRecSelectionActive) {
      return `This product is unavailable in your selected ${startCase(
        customer_type?.toLowerCase(),
      )} inventory type`;
    }

    warning = 'This product is unavailable in your selected store';

    if (isMedAndRecSelectionActive) {
      warning += ` or ${startCase(
        customer_type?.toLowerCase(),
      )} inventory type`;
    }

    return warning;
  };

  const UnavailableMessage = (): JSX.Element | null => {
    if (!hasStore) return null;

    if ((isProductAvailable || isCheckingAvailability) && inStock) {
      return null;
    }

    const isSingleStore = treezStores.length === 1;

    const warning = warningMessagge(isSingleStore);

    return (
      <div className={'details__availability'}>
        <Alert isOpen warning>
          <div className="details__availability_message">
            {warning}
            {!isSingleStore && (
              <div className="details__availability_message__actions">
                <Button
                  className="location_button"
                  color="link"
                  onClick={showStoresSelect}
                  size="sm"
                >
                  Change location
                </Button>
              </div>
            )}
          </div>
        </Alert>
      </div>
    );
  };

  const productName = product?.name || 'this product';
  const favoriteAriaLabel = isFavorite
    ? `Remove ${productName} from favorites`
    : `Add ${productName} to favorites`;

  const priceAndFavAction = (
    <div
      aria-label="Product price and favorite action"
      className={'detail__product_actions'}
      role="group"
    >
      <Price
        price={`$${currencyFormat(
          unitTierPricing?.originalCents ?? currentWeightVariant?.price!,
        )}`}
        salePrice={`$${currencyFormat(
          unitTierPricing?.saleCents ?? currentWeightVariant?.salePrice!,
        )}`}
        special={isSpecial}
      />

      {wishlistActive && (
        <Button
          ariaLabel={favoriteAriaLabel}
          className="favorite_icon"
          color="tertiary"
          onClick={() => {
            handleFavorite(product);
          }}
        >
          <Icon
            aria-hidden="true"
            name={isFavorite ? 'favorite-solid' : 'favorite'}
          />
        </Button>
      )}
    </div>
  );

  const continueShopping = (
    <Link
      aria-label={'Go to continue shopping'}
      className={'details__item-link'}
      href={TEMPLATE_BASE_PATH.SHOP}
    >
      Continue Shopping
      <IconSvg name="arrow-right" />
    </Link>
  );

  if (!hasStore) {
    return (
      <div className={'details__item-control'}>
        <div className={'details__item'}>
          <ChooseStoreButton />
        </div>
      </div>
    );
  }

  return (
    <div className={'details__item-control'}>
      {isCheckingAvailability && (
        <div
          className={classNames('availability_placeholders', {
            hidden: !isCheckingAvailability,
          })}
        >
          <Placeholder />
          <Placeholder />
        </div>
      )}
      {!isCheckingAvailability && (
        <>
          <div
            className={classNames('details__item', {
              hidden: !isProductAvailable || isCheckingAvailability,
            })}
          >
            {inStock && (
              <Counter
                actionTheme="primary"
                //callbackIfBundle={handleBundleCallback}
                //handleBundlePopup={handleBundlePopup}
                onDetail
                product={product}
                renderContinueShopping={keepShoppingActive && continueShopping}
                renderPriceFavAction={priceAndFavAction}
                renderWeightVariant={
                  <WeightVariants
                    currentWeightVariant={currentWeightVariant!}
                    handleWeightVariatChange={setCurrentWeightVariant}
                    product={product}
                  />
                }
                selectedWeightVariant={currentWeightVariant}
                showIcon={true}
              />
            )}
            {/*  <BundlePopup
          bundleId={bundleId}
          bundleJaneSpecial={bundleJaneSpecial as JaneSpecial}
          bundleProducts={bundleProducts}
          onClose={() => {
            setShowBundlePopup(false);
            setBundleId(undefined);
          }}
          open={showBundlePopup}
          ref={bundlePanelRef}
        /> */}
          </div>
          <UnavailableMessage />
        </>
      )}
    </div>
  );
};

export { ProductControls };
